import {Trans} from '@lingui/macro';
import React from 'react';
import styled from 'styled-components';

import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../libs/shared/ui/src/styles/mixins';
import {boxShadow} from '../../../../libs/shared/ui/src/theme/themeStyleHelpers';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useAppDispatch} from '../../../helpers/reduxHelpers';
import {RuleBundleTypesEnum} from '../../../models/api/ruleCommonTypes';
import {RuleModelStrict} from '../../../models/api/ruleModel';
import {navigateToLinkAction} from '../../../store/stateless/navigationActions';
import {hasUnrestrictedInboxScope} from '../ruleHelpers';
import {RuleListIcon} from './ruleListIcon';

/*
 * Type.
 */

interface RuleListItemProps {
  rule: RuleModelStrict;
}

/*
 * Styles.
 */

export const StyledRuleListWrap = styled.div`
  max-width: 600px;
  margin: 0 auto 20px;
  display: grid;
  grid-column-gap: 20px;
  align-items: center;
  border: 1px solid ${(p) => p.theme.alphas.black20};
  border-radius: 16px;
  ${(p) => boxShadow(p.theme, `0px 1px 9px rgb(0 0 0 / 8%)`)};
  padding: 17px 20px 18px 20px;
  grid-template-columns: 40px auto 30px;
  grid-template-areas:
    'icon title toggle'
    'icon description toggle';

  &:last-child {
    margin-bottom: 0;
  }
`;

export const StyledRuleListWrapIcon = styled.div`
  grid-area: icon;
`;

export const StyledRuleListWrapTitle = styled.div`
  grid-area: title;
  font-weight: ${fontWeights.semibold};
  font-size: ${fontSizes.large};
  padding-bottom: 5px;
`;

export const StyledRuleListWrapDescription = styled.div`
  grid-area: description;
  display: flex;
  align-items: center;
`;

const StyledInboxScopeWarningWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-right: 10px;
  font-size: ${fontSizes.medium};
  white-space: nowrap;
  color: ${(p) => p.theme.palette.orange.shade40};
  ${ellipsis()}

  svg {
    margin-right: 4px;
  }
`;

/*
 * Component.
 */

export const RuleListItem = ({rule}: RuleListItemProps) => {
  const theme = useUserTheme();
  const dispatch = useAppDispatch();

  const navigateToRule = () => {
    if (rule.bundle.type === RuleBundleTypesEnum.SLA) {
      return dispatch(navigateToLinkAction(`/settings/${rule.namespace}/rules/recipes/${rule.id}`));
    }

    return dispatch(navigateToLinkAction(`/settings/${rule.namespace}/rules/edit/${rule.id}`));
  };

  const maybeRenderInboxScopeWarning = () => {
    if (!hasUnrestrictedInboxScope(rule)) {
      return null;
    }

    return (
      <StyledInboxScopeWarningWrapper>
        <Icon name="warningFilledSmall" color={theme.palette.orange.shade40} />
        <Trans id="rule-list-applies-to-all-inboxes">Applies on all inboxes</Trans>
      </StyledInboxScopeWarningWrapper>
    );
  };

  return (
    <StyledRuleListWrap onClick={navigateToRule}>
      <StyledRuleListWrapIcon>
        <RuleListIcon rule={rule} />
      </StyledRuleListWrapIcon>
      <StyledRuleListWrapTitle>{rule.name}</StyledRuleListWrapTitle>
      <StyledRuleListWrapDescription>
        {maybeRenderInboxScopeWarning()}
        {rule.description}
      </StyledRuleListWrapDescription>
    </StyledRuleListWrap>
  );
};
